import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import navigationConfig from 'configs/navigation.config'
import {
    NAV_ITEM_TYPE_ITEM,
    NAV_ITEM_TYPE_COLLAPSE,
    NAV_ITEM_TYPE_TITLE,
} from 'constants/navigation.constant'
import { HiOutlineSearch, HiOutlineArrowSmRight } from 'react-icons/hi'
import { apiGetSalesOrders, apiGetUsers } from 'services/SalesService'
import { apiGetCatalogueDirectory } from 'services/CatalogueService'

/**
 * CommandPalette (Dashboard Redesign, Phase 5 + audit P1-1).
 * ⌘K / Ctrl+K quick navigator — pages from the sidebar tree, PLUS live
 * records: type an order number, customer name/email or product title and
 * jump straight to it. Entity lookups hit the same admin list endpoints
 * the workspaces use (5 results each, debounced, admin-only), so the
 * palette can never show anything the signed-in role couldn't open.
 */

// Entity search is ADMIN-scoped: the three list endpoints are restrictTo
// admin — support staff keep the page navigator only.
const canSearchEntities = (authority) => (authority || []).includes('admin')

const entityFetchers = (q) => [
    apiGetSalesOrders({ query: q, pageIndex: 1, pageSize: 5 }).then((res) =>
        (res?.data?.data?.items || []).map((o) => ({
            kind: 'Orders',
            title: `#${o.orderNumber || String(o._id).slice(-6)}`,
            subtitle: [
                [o.user?.profile?.firstName, o.user?.profile?.lastName].filter(Boolean).join(' ') || o.user?.username,
                o.status ? String(o.status).replace(/_/g, ' ') : null,
            ].filter(Boolean).join(' · '),
            path: `/app/sales/order-details/${o._id}`,
        }))
    ),
    apiGetUsers({ search: q, page: 1, limit: 5 }).then((res) =>
        (res?.data?.data?.users || []).map((u) => ({
            kind: 'Customers',
            title: [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(' ') || u.username || u.email,
            subtitle: u.email || u.profile?.phone || '',
            path: `/app/customers/${u._id}`,
        }))
    ),
    apiGetCatalogueDirectory({ q, page: 1, limit: 5 }).then((res) =>
        (res?.data?.data?.items || []).map((p) => ({
            kind: 'Products',
            title: p.title || p.slug,
            subtitle: p.brandName || '',
            path: `/app/catalog/products/${p.id}`,
        }))
    ),
]

// Flatten the nav tree into a searchable list of navigable destinations,
// filtered by the user's authority and tagged with their parent group.
const flattenNav = (nodes, userAuthority, group = '') => {
    const out = []
    for (const node of nodes || []) {
        const allowed =
            !node.authority?.length ||
            node.authority.some((a) => userAuthority.includes(a))
        if (!allowed) continue

        const isGroup =
            node.type === NAV_ITEM_TYPE_COLLAPSE ||
            node.type === NAV_ITEM_TYPE_TITLE
        const nextGroup = isGroup && node.title ? node.title : group

        if (node.type === NAV_ITEM_TYPE_ITEM && node.path) {
            out.push({ title: node.title, path: node.path, group })
        }
        if (node.subMenu?.length) {
            out.push(...flattenNav(node.subMenu, userAuthority, nextGroup))
        }
    }
    return out
}

const CommandPalette = () => {
    const navigate = useNavigate()
    const userAuthority = useSelector((state) => state.auth?.user?.authority) || []
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [active, setActive] = useState(0)
    const inputRef = useRef(null)
    const listRef = useRef(null)

    const destinations = useMemo(
        () => flattenNav(navigationConfig, userAuthority),
        [userAuthority]
    )

    // Live record lookups (orders / customers / products), debounced.
    const [entityResults, setEntityResults] = useState([])
    const [entityLoading, setEntityLoading] = useState(false)
    useEffect(() => {
        const q = query.trim()
        if (!open || q.length < 2 || !canSearchEntities(userAuthority)) {
            setEntityResults([])
            setEntityLoading(false)
            return undefined
        }
        let stale = false
        setEntityLoading(true)
        const timer = setTimeout(async () => {
            const settled = await Promise.allSettled(entityFetchers(q))
            if (stale) return
            setEntityResults(settled.flatMap((s) => (s.status === 'fulfilled' ? s.value : [])))
            setEntityLoading(false)
        }, 300)
        return () => {
            stale = true
            clearTimeout(timer)
        }
    }, [query, open, userAuthority])

    const results = useMemo(() => {
        const q = query.trim().toLowerCase()
        const pages = (!q
            ? destinations.slice(0, 12)
            : destinations
                  .filter((d) => `${d.title} ${d.group}`.toLowerCase().includes(q))
                  .slice(0, entityResults.length ? 6 : 20)
        ).map((d) => ({ kind: 'Pages', title: d.title, subtitle: d.group, path: d.path }))
        return [...pages, ...entityResults]
    }, [query, destinations, entityResults])

    const close = useCallback(() => {
        setOpen(false)
        setQuery('')
        setActive(0)
    }, [])

    const go = useCallback(
        (dest) => {
            if (!dest) return
            close()
            navigate(dest.path)
        },
        [navigate, close]
    )

    // Global ⌘K / Ctrl+K toggle + Esc to close; the header search icon
    // opens it via a custom event (components/template/Search.js).
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault()
                setOpen((o) => !o)
            } else if (e.key === 'Escape') {
                setOpen(false)
            }
        }
        const onOpenEvent = () => setOpen(true)
        window.addEventListener('keydown', onKey)
        window.addEventListener('wearit:open-command-palette', onOpenEvent)
        return () => {
            window.removeEventListener('keydown', onKey)
            window.removeEventListener('wearit:open-command-palette', onOpenEvent)
        }
    }, [])

    useEffect(() => {
        if (open) {
            setActive(0)
            // Focus the input on next tick once mounted.
            setTimeout(() => inputRef.current?.focus(), 0)
        }
    }, [open])

    useEffect(() => {
        setActive(0)
    }, [query])

    const onListKey = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((i) => Math.min(i + 1, results.length - 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((i) => Math.max(i - 1, 0))
        } else if (e.key === 'Enter') {
            e.preventDefault()
            go(results[active])
        }
    }

    if (!open) return null

    return (
        <div
            className="fixed inset-0 z-[3000] flex items-start justify-center bg-black/40 pt-[12vh] backdrop-blur-sm"
            onMouseDown={close}
        >
            <div
                className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-gray-800"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-3 border-b border-gray-100 px-4 dark:border-gray-700">
                    <HiOutlineSearch className="text-lg text-gray-400" />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onListKey}
                        placeholder="Jump to a page, order #, customer or product…"
                        className="w-full bg-transparent py-4 text-sm outline-none placeholder:text-gray-400 dark:text-gray-100"
                    />
                    <kbd className="hidden rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-400 sm:inline dark:border-gray-600">
                        ESC
                    </kbd>
                </div>

                <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-2">
                    {results.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-gray-400">
                            {entityLoading ? 'Searching…' : `No matches for “${query}”.`}
                        </div>
                    ) : (
                        results.map((d, i) => (
                            <React.Fragment key={`${d.kind}-${d.path}-${i}`}>
                                {(i === 0 || results[i - 1].kind !== d.kind) && (
                                    <p className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                                        {d.kind}
                                        {d.kind !== 'Pages' && entityLoading ? ' · updating…' : ''}
                                    </p>
                                )}
                                <button
                                    onMouseEnter={() => setActive(i)}
                                    onClick={() => go(d)}
                                    className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm ${
                                        i === active
                                            ? 'bg-indigo-50 dark:bg-indigo-500/10'
                                            : ''
                                    }`}
                                >
                                    <span className="flex min-w-0 items-center gap-2">
                                        <span className="truncate font-medium text-gray-800 dark:text-gray-100">
                                            {d.title}
                                        </span>
                                        {d.subtitle && (
                                            <span className="truncate text-xs text-gray-400">
                                                · {d.subtitle}
                                            </span>
                                        )}
                                    </span>
                                    {i === active && (
                                        <HiOutlineArrowSmRight className="shrink-0 text-indigo-500" />
                                    )}
                                </button>
                            </React.Fragment>
                        ))
                    )}
                </div>

                <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2 text-[11px] text-gray-400 dark:border-gray-700">
                    <span>↑↓ to navigate · ↵ to open</span>
                    <span>⌘K</span>
                </div>
            </div>
        </div>
    )
}

export default CommandPalette
